import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Hash } from 'lucide-react';
import type { IdentifierRow } from '@/utils/canonicalTransform';

interface IdentifierTableProps {
  data: IdentifierRow[];
  onEdit?: (field: string, value: any) => void;
}

export function IdentifierTable({ data, onEdit }: IdentifierTableProps) {
  const formatValue = (value: any, type: 'money' | 'number' | 'text' | 'date' = 'text') => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'money') {
      return typeof value === 'number' ? `$${value.toFixed(2)}` : `$${value}`;
    }
    
    if (type === 'number') {
      return typeof value === 'number' ? value.toLocaleString() : value;
    }
    
    if (type === 'date') {
      return String(value);
    }
    
    return String(value);
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Hash className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p>No identifier data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-pink-600" />
          <h3 className="text-sm font-medium text-gray-900">Service Identifiers</h3>
          <Badge variant="secondary" className="text-xs">
            {data.length} {data.length === 1 ? 'identifier' : 'identifiers'}
          </Badge>
        </div>
      </div>
      
      <div className="overflow-auto max-h-[350px] force-scrollbar">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Identifier
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Utility Type
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Site Address
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                City
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                State
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Country
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Postal Code
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Period From
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Period To
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Service Total
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-gray-200">
            {data.map((identifier, index) => (
              <TableRow key={index} className="hover:bg-gray-50">
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap font-medium">
                  {formatValue(identifier.identifier)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.utilityType)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.siteAddress)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.siteCity)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.siteState)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.siteCountry)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.sitePostCode)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.periodFrom, 'date')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(identifier.periodTo, 'date')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap font-medium">
                  {formatValue(identifier.serviceTotal, 'money')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
